import { useQuery } from "@apollo/client/react";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, Card, Checkbox, Empty, Flex, Space, Typography } from "antd";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GetRecipeDeclarationDocument } from "../../../gql/generated/graphqlSchema.tsx";
import { DeclarationView } from "../../../components/DeclarationView.tsx";
import { useRecipe } from "../RecipeContext.tsx";
import { NutritionDeclaration } from "../../../components/NutritionDeclaration.tsx";
import { PageLoader } from "../../../components/PageLoader.tsx";

export const RecipeLabelTab = () => {
  const { t, i18n } = useTranslation();
  const { recipe } = useRecipe();
  const [uppercaseAllergens, setUppercaseAllergens] = useState(false);
  const { data, loading } = useQuery(GetRecipeDeclarationDocument, {
    variables: { id: recipe.id, versionNumber: recipe.versionNumber },
  });
  const declaration = data?.recipeDeclaration;

  const allergens = useMemo(() => {
    const derived = declaration?.derivedAllergens ?? [];
    return [...derived]
      .map((a) =>
        uppercaseAllergens
          ? t(`allergens.${a}`).toLocaleUpperCase(i18n.language)
          : t(`allergens.${a}`).toLocaleLowerCase(i18n.language),
      )
      .sort((a, b) => a.localeCompare(b, i18n.language));
  }, [declaration?.derivedAllergens, t, uppercaseAllergens, i18n.language]);

  if (loading) return <PageLoader />;

  const hasEntries = declaration != null && declaration.rootIndices.length > 0;

  if (!hasEntries && recipe.nutritionProfile == null) {
    return <Empty description={t("recipes.label.noIngredients")} />;
  }

  return (
    <Flex gap="middle" vertical>
      <Space wrap>
        <Button
          color="primary"
          href={`${recipe.labelExportUrl}?lang=${i18n.language}&uppercaseAllergens=${uppercaseAllergens}`}
          icon={<DownloadOutlined />}
          variant="outlined"
        >
          {t("recipes.label.export")}
        </Button>
        <Checkbox checked={uppercaseAllergens} onChange={(e) => setUppercaseAllergens(e.target.checked)}>
          {t("recipes.label.uppercaseAllergens")}
        </Checkbox>
      </Space>
      <Card>
        <Flex gap="middle" vertical>
          {hasEntries && declaration ? (
            <Typography.Text>
              <strong>{t("ingredients.labelIngredients.ingredientsPrefix")}</strong>{" "}
              <DeclarationView
                entries={declaration.entries}
                indices={declaration.rootIndices}
                uppercaseAllergens={uppercaseAllergens}
              />
            </Typography.Text>
          ) : null}
          {allergens.length > 0 ? (
            <Typography.Text>
              <strong>{t("ingredients.labelIngredients.allergensPrefix")}</strong> {allergens.join(", ")}
            </Typography.Text>
          ) : null}
          <NutritionDeclaration nutritionProfile={recipe.nutritionProfile} />
          {recipe.storageRequirements ? (
            <Typography.Text>
              <strong>{t("recipes.storageRequirements")}:</strong> {recipe.storageRequirements}
            </Typography.Text>
          ) : null}
          {recipe.producer ? (
            <Typography.Text>
              <strong>{t("ingredients.labelIngredients.producerPrefix")}</strong>{" "}
              {[
                recipe.producer.companyName,
                recipe.producer.address,
                recipe.producer.phone
                  ? `${t("ingredients.labelIngredients.phonePrefix")}${recipe.producer.phone}`
                  : null,
                recipe.producer.email
                  ? `${t("ingredients.labelIngredients.emailPrefix")}${recipe.producer.email}`
                  : null,
                recipe.producer.website
                  ? `${t("ingredients.labelIngredients.websitePrefix")}${recipe.producer.website}`
                  : null,
              ]
                .filter((v): v is string => v != null && v.length > 0)
                .join(", ")}
            </Typography.Text>
          ) : null}
        </Flex>
      </Card>
    </Flex>
  );
};
